/* ==========================================================================
   FLC Forge — Header (Mega Menu + Mobile Menu)
   ========================================================================== */

class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.items    = this.querySelectorAll('[data-mega-item]');
    this.panels   = this.querySelectorAll('[data-mega-panel]');
    this.closeTimer = null;

    this.items.forEach(item => {
      const itemId = item.dataset.megaItem;
      const panelEl = this.querySelector(`[data-mega-panel="${itemId}"]`);

      if (!panelEl) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          this.closeAll(false);
        });
        return;
      }

      item.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimer);
        this.openPanel(item, panelEl);
      });

      // Keyboard
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          panelEl?.classList.contains('is-open') ? this.closeAll() : this.openPanel(item, panelEl);
        }
        if (e.key === 'Escape') this.closeAll();
      });
    });

    this.header = this.closest('header') || document.querySelector('.site-header');

    // True when the pointer is in the header's padding directly above/below the
    // nav links (same horizontal span), i.e. it slipped off an anchor but is
    // still effectively on the nav.
    this._inNavColumn = (event) => {
      if (!this.header || !this.header.contains(event.target)) return false;
      const rect = this.getBoundingClientRect();
      return event.clientX >= rect.left && event.clientX <= rect.right;
    };

    this.addEventListener('mouseleave', (event) => {
      if ([...this.panels].some((panel) => panel.contains(event.relatedTarget))) return;
      if (this._inNavColumn({ target: event.relatedTarget, clientX: event.clientX })) return;
      this.closeTimer = setTimeout(() => this.closeAll(), 180);
    });

    // Slow hover: while a panel is open, keep it open as long as the pointer
    // stays in the nav column; close shortly after it drifts out sideways.
    this.header?.addEventListener('mousemove', (event) => {
      if (![...this.panels].some((panel) => panel.classList.contains('is-open'))) return;
      if (this._inNavColumn(event) || [...this.panels].some((panel) => panel.contains(event.target))) {
        clearTimeout(this.closeTimer);
        this._driftTimer = false;
      } else if (!this._driftTimer) {
        this._driftTimer = true;
        this.closeTimer = setTimeout(() => { this._driftTimer = false; this.closeAll(); }, 180);
      }
    });
    this.header?.addEventListener('mouseleave', (event) => {
      if ([...this.panels].some((panel) => panel.contains(event.relatedTarget))) return;
      clearTimeout(this.closeTimer);
      this._driftTimer = false;
      this.closeTimer = setTimeout(() => this.closeAll(), 180);
    });
    this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    this.panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
      panel.addEventListener('mouseleave', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.closeTimer = setTimeout(() => this.closeAll(), 180);
      });
    });

    this._onDocClick     = (e) => { if (!this.contains(e.target)) this.closeAll(false); };
    this._onDocKeydown   = (e) => { if (e.key === 'Escape') this.closeAll(); };
    this._onDrawersClose = () => this.closeAll();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onDocKeydown);
    document.addEventListener('flc:drawers:close-all', this._onDrawersClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onDocKeydown);
    document.removeEventListener('flc:drawers:close-all', this._onDrawersClose);
  }

  openPanel(item, panel) {
    this.closeAll(false);
    if (!panel) return;
    item.setAttribute('aria-expanded', 'true');
    // make panel visible to compute dimensions
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');

    // Position panel under the triggering menu item
    try {
      const itemRect = item.getBoundingClientRect();
      const headerEl = this.closest('header') || document.querySelector('.site-header');
      const headerRect = headerEl ? headerEl.getBoundingClientRect() : { bottom: 0 };
      panel.style.position = 'fixed';
      panel.style.top = `${headerRect.bottom}px`;

      if (panel.classList.contains('mega-menu__panel--mega')) {
        panel.style.left = '0';
        panel.style.right = '0';
        panel.style.width = '100vw';
      } else {
        const panelWidth = panel.offsetWidth || Math.min(window.innerWidth, 800);
        let left = itemRect.left + (itemRect.width / 2) - (panelWidth / 2);
        left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
        panel.style.left = `${left}px`;
        panel.style.right = '';
        panel.style.width = '';
      }
    } catch (e) {
      // positioning failed silently
    }

  }

  closeAll(closeOverlay = true) {
    this.items.forEach(item => item.setAttribute('aria-expanded', 'false'));
    this.panels.forEach(panel => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.left = '';
      panel.style.right = '';
      panel.style.top = '';
      panel.style.width = '';
      panel.style.position = '';
    });
    if (closeOverlay) FLC.closeOverlay('mega-menu');
  }
}

customElements.define('mega-menu', MegaMenu);

/* Mobile menu drawer — slide-panel (L1) + accordion (L2+)
   ========================================================================== */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.openBtn   = document.querySelector('[data-mobile-menu-open]');
    this.closeBtn  = this.querySelector('[data-mobile-menu-close]');
    this.mainPanel = this.querySelector('.mobile-menu__panel--main');

    this._onDrawersClose = () => this.close();
    this._onKeydown      = (e) => { if (e.key === 'Escape') this.close(); };

    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('flc:drawers:close-all', this._onDrawersClose);
    document.addEventListener('keydown', this._onKeydown);

    // L1 → L2: slide to sub-panel
    this.querySelectorAll('[data-panel-trigger]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPanel = this.querySelector(`[data-panel-id="${btn.dataset.panelTarget}"]`);
        if (!targetPanel) return;
        this.mainPanel.classList.add('is-behind');
        targetPanel.classList.add('is-active');
        btn.setAttribute('aria-expanded', 'true');
      });
    });

    // Back button: sub-panel → main panel
    this.querySelectorAll('[data-panel-back]').forEach(btn => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.mobile-menu__panel');
        panel.classList.remove('is-active');
        this.mainPanel.classList.remove('is-behind');
        const panelId = panel.dataset.panelId;
        this.querySelector(`[data-panel-target="${panelId}"]`)?.setAttribute('aria-expanded', 'false');
      });
    });

    // L2 → L3: accordion in sub-panels
    this.querySelectorAll('[data-sub-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const sub    = btn.nextElementSibling;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isOpen));
        sub?.classList.toggle('is-open', !isOpen);
      });
    });
  }

  open() {
    this.classList.add('is-open');
    FLC.openOverlay('mobile-menu');
    FLC.trapFocus(this);
    document.querySelector('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    FLC.closeOverlay('mobile-menu');
    document.querySelector('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'false');
    // Reset sub-panels after close transition
    setTimeout(() => {
      this.querySelectorAll('.mobile-menu__panel.is-active').forEach(p => p.classList.remove('is-active'));
      this.mainPanel?.classList.remove('is-behind');
      this.querySelectorAll('[data-panel-trigger]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    }, 320);
  }

  disconnectedCallback() {
    document.removeEventListener('flc:drawers:close-all', this._onDrawersClose);
    document.removeEventListener('keydown', this._onKeydown);
  }
}

customElements.define('mobile-menu', MobileMenu);

/* Cart icon — open drawer on click
   ========================================================================== */
document.querySelector('[data-cart-icon-bubble]')?.addEventListener('click', (e) => {
  if (window.flcForge.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
    e.preventDefault();
    FLC.dispatch('flc:cart:open');
  }
});
